import { useState, useEffect, useCallback } from "react"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import Table, { TCellItem } from "~/ui-rtk/components/ui/common/Table"
import { Chip } from "~/ui-rtk/components/ui/common"
import { Input, Select, Button } from "~/ui-rtk/components/ui/controls"
import { DataSaverSvg } from "~/ui-rtk/components/ui/svg/essentials"
import {
  MetricCategory,
  MetricPlatform,
  ChannelType,
  BusinessArea,
  CATEGORY_COLORS,
  CATEGORY_BG_COLORS,
  CHANNEL_TYPE_COLORS,
  PLATFORM_DISPLAY_NAMES,
} from "~/ui-rtk/constants/metric-dictionary"
import {
  exportTableToCSV,
  SerializableColumnConfig,
} from "~/ui-rtk/utils/csv-export"
import useConnect from "./connect"

const categoryOptions = [
  { label: "All Categories", value: "" },
  ...Object.values(MetricCategory).map(cat => ({ label: cat, value: cat })),
]

const platformOptions = [
  { label: "All Platforms", value: "" },
  ...Object.values(MetricPlatform).map(plat => ({
    label: PLATFORM_DISPLAY_NAMES[plat],
    value: plat,
  })),
]

const channelTypeOptions = [
  { label: "All Channel Types", value: "" },
  ...Object.values(ChannelType).map(ct => ({ label: ct, value: ct })),
]

const businessAreaOptions = [
  { label: "All Business Areas", value: "" },
  ...Object.values(BusinessArea).map(ba => ({ label: ba, value: ba })),
]

const renderCategoryChips = (item: TCellItem) => {
  const categories = item.category as MetricCategory[]
  return (
    <div className="flex flex-wrap gap-1">
      {categories.map(cat => (
        <Chip
          key={cat}
          className="text-2.5 leading-3 rounded px-2 py-1"
          style={{
            backgroundColor: CATEGORY_BG_COLORS[cat],
            color: CATEGORY_COLORS[cat],
          }}
        >
          {cat}
        </Chip>
      ))}
    </div>
  )
}

const renderChannelChip = (item: TCellItem) => {
  const channelType = item.channelType as ChannelType
  return (
    <Chip
      className="text-2.5 leading-3 rounded px-2 py-1"
      style={{
        backgroundColor: `${CHANNEL_TYPE_COLORS[channelType]}20`,
        color: CHANNEL_TYPE_COLORS[channelType],
      }}
    >
      {channelType}
    </Chip>
  )
}

const renderKey = (item: TCellItem) => (
  <code className="text-3 font-mono text-basic-grey-inactive">{item.key}</code>
)

const renderDescription = (item: TCellItem) => {
  const description = item.description as string | undefined
  if (!description) return <span className="text-basic-grey-inactive">-</span>
  return <span className="text-3 text-basic-grey-inactive">{description}</span>
}

const renderPlatform = (item: TCellItem) => {
  const platform = item.platform as MetricPlatform
  return <span>{PLATFORM_DISPLAY_NAMES[platform]}</span>
}

const renderFormat = (item: TCellItem) => {
  const format = item.format as string | undefined
  if (!format) return <span className="text-basic-grey-inactive">-</span>
  return <span className="capitalize">{format}</span>
}

export default function MetricDictionaryPage() {
  const {
    metrics,
    totalCount,
    isLoading,
    filters,
    hasActiveFilters,
    handleFilterChange,
    handleSearchChange,
    handleClearFilters,
  } = useConnect()

  const [searchInput, setSearchInput] = useState("")

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearchChange(searchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, handleSearchChange])

  const handleExportCSV = useCallback(() => {
    if (metrics.length === 0) return

    const csvColumnConfig: SerializableColumnConfig[] = [
      { key: "key", header: "Key", type: "string" },
      { key: "title", header: "Title", type: "string" },
      { key: "description", header: "Description", type: "string" },
      { key: "category", header: "Category", type: "string" },
      { key: "platform", header: "Platform", type: "string" },
      { key: "channelType", header: "Channel Type", type: "string" },
      { key: "format", header: "Format", type: "string" },
    ]

    const csvData = metrics.map(metric => ({
      key: metric.key,
      title: metric.title,
      description: metric.description || "",
      category: metric.category.join(", "),
      platform: PLATFORM_DISPLAY_NAMES[metric.platform],
      channelType: metric.channelType,
      format: metric.format || "",
    }))

    const dateStr = new Date().toISOString().split("T")[0]
    exportTableToCSV(csvData, csvColumnConfig, {
      filename: `metrics-dictionary-${dateStr}.csv`,
      includeHeaders: true,
    })
  }, [metrics])

  const headers = [
    {
      label: "Key",
      customRender: renderKey,
      width: 200,
    },
    {
      label: "Title",
      value: "title",
      width: 200,
    },
    {
      label: "Description",
      customRender: renderDescription,
    },
    {
      label: "Category",
      customRender: renderCategoryChips,
      width: 180,
    },
    {
      label: "Platform",
      customRender: renderPlatform,
      width: 120,
    },
    {
      label: "Channel",
      customRender: renderChannelChip,
      width: 100,
    },
    {
      label: "Format",
      customRender: renderFormat,
      width: 100,
    },
  ]

  let content
  if (isLoading) {
    content = (
      <div className="flex justify-center py-12">
        <DataSaverSvg className="animate-spin" />
      </div>
    )
  } else if (metrics.length === 0) {
    content = (
      <div className="flex flex-col items-center justify-center py-12 text-basic-grey-inactive">
        <P>No metrics found</P>
        {hasActiveFilters && (
          <Button
            variant={{ variant: "text" }}
            className="mt-2 text-pink-500"
            onClick={handleClearFilters}
          >
            Clear filters
          </Button>
        )}
      </div>
    )
  } else {
    content = (
      <Table
        headers={headers}
        items={metrics}
        classes={{
          th: "text-3.5",
          td: "text-3.5 py-3",
        }}
      />
    )
  }

  return (
    <div className="py-6">
      <H3>Metric Dictionary</H3>
      <P className="my-3 text-basic-grey-inactive">
        Browse and search all available metrics in the system.
        {!isLoading && ` Showing ${metrics.length} of ${totalCount} metrics.`}
      </P>

      <div className="flex flex-wrap gap-4 my-6">
        <div className="w-64">
          <Input
            placeholder="Search metrics..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="bg-basic-dark-blue border-basic-blue"
          />
        </div>
        <div className="w-44">
          <Select
            options={categoryOptions}
            value={filters.category || ""}
            onChange={e =>
              handleFilterChange(
                "category",
                (e.target.value as MetricCategory) || undefined,
              )
            }
          />
        </div>
        <div className="w-44">
          <Select
            options={platformOptions}
            value={filters.platform || ""}
            onChange={e =>
              handleFilterChange(
                "platform",
                (e.target.value as MetricPlatform) || undefined,
              )
            }
          />
        </div>
        <div className="w-44">
          <Select
            options={channelTypeOptions}
            value={filters.channelType || ""}
            onChange={e =>
              handleFilterChange(
                "channelType",
                (e.target.value as ChannelType) || undefined,
              )
            }
          />
        </div>
        <div className="w-44">
          <Select
            options={businessAreaOptions}
            value={filters.businessArea || ""}
            onChange={e =>
              handleFilterChange(
                "businessArea",
                (e.target.value as BusinessArea) || undefined,
              )
            }
          />
        </div>
        {hasActiveFilters && (
          <Button
            variant={{ variant: "text" }}
            className="text-pink-500"
            onClick={handleClearFilters}
          >
            Clear all
          </Button>
        )}
        <div className="flex-grow" />
        <Button
          variant={{ variant: "solid", color: "blue" }}
          onClick={handleExportCSV}
          disabled={isLoading || metrics.length === 0}
        >
          Export CSV
        </Button>
      </div>

      {content}
    </div>
  )
}
